// Gallery-page theming engine (#1709 · Tier-L). Single seam between a persisted
// theme shape and the rendered Gallery. Value-agnostic: works with ANY
// bg_color / wallpaper / accent the user picks, resolves curated PRESETS
// (Iris/Dusk/Bloom), and leaves a clean hook for more presets without touching
// the renderer or the persistence layer.
//
// Mirrors the LinkVault "Vault Design" engine (vaultTheme.js) deliberately: same
// CSS-injection guards, same preset-override precedence, same
// unset===neutral-passthrough contract. Kept self-contained (its own guards,
// not imported) so this addition is purely additive and independently testable.
//
// Shape persisted on UserProfile.gallery_theme (backend, additive nullable JSON):
//   {
//     preset?: "iris" | "dusk" | "bloom",
//     wallpaper?: string | { url: string, source?: string },
//       // string: css color / gradient / a generated SVG-pattern `background`
//       //   value (see resolvePatternWallpaperCss below — the SAME
//       //   url("data:image/svg+xml,...") shorthand the Edit-Wheel builder's
//       //   "SVG Patterns" tab already writes into its own bg_wallpaper string
//       //   slot, so no BE schema change) / a legacy plain http(s) image URL
//       //   (back-compat: pre-parity saves) / "" (none).
//       // object: { url, source } — a real media reference (Upload / Media
//       //   Library / Recent / Embed URL all resolve to this shape, mirroring
//       //   the Edit-Wheel builder's upload flow, ISP-9999
//       //   gordon-0913-gallery-theme-wallpaper-pattern-image-parity). `source`
//       //   is informational only ("upload"|"library"|"recent"|"url").
//     accent?: string,              // css color literal
//     bg_color?: string,            // css color literal
//     card_radius?: number,         // advanced: card corner radius (px, 0..32)
//     match_link_vault?: boolean    // advanced: inherit the saved vault_theme
//   }
// NULL / undefined / {} === NEUTRAL PASSTHROUGH: today's exact Gallery look.

import {
  autoTextColor,
  autoAccentInk,
  legibleAccent,
  hexToRgba,
  WCAG_AA_LARGE,
  effectiveBgHex,
  INK_TEXT,
  WHITE_TEXT,
  BLACK_TEXT,
} from "../../utils/contrast";
import { colors } from "../../utils";

// PATTERN TAB SOURCE OF TRUTH (ISP-9999 gordon-0913-gallery-theme-wallpaper-
// pattern-image-parity — corrected after STEP-1 measurement): the Edit-Wheel
// builder's OLD 28-pattern <Pattern/> mask component (Wallpaper/children/
// Patterns) is DEAD CODE in the live picker — its "Patterns" tab is filtered
// out of the rendered tab strip on every caller
// (Wallpaper.patternsTabUnreachable.dom.test.jsx pins this as a fact). The
// pattern experience users actually reach today is the 45-pattern
// "SVG Patterns" library (BackgroundPatternPanel, REACT_APP_BG_PATTERNS_ENABLED
// — "true" on Dev per .github/workflows/DevOps-Dev.yml), which composes its own
// name/scale/colour-picked selection into ONE `background` CSS shorthand value
// via buildPatternCss and writes it through the SAME onApply(cssString) slot the
// Gradient tab uses. Gallery Theme reuses BackgroundPatternPanel itself (see
// GalleryThemePanel.jsx) rather than the dead component.
import { isPatternCss, parsePatternCss, buildPatternCss } from "../backgroundPatterns/toBackgroundCss";
import { isSafeColor as isSafePatternColor } from "../backgroundPatterns/patternGenerators";

// A generated pattern's CSS is `url("data:image/svg+xml,...") repeat, <bg>` —
// it legitimately contains `url(`, which isSafeGradient/isSafeCssBackground
// below deliberately reject for every OTHER wallpaper string (that ban is what
// stops a pasted/tampered string from smuggling an arbitrary url() into the
// DOM). Patterns get a narrow, safe exception: never trust the raw persisted
// bytes — parse them back into {id, fg, bg, scale} (parsePatternCss already
// validates the id against PATTERN_BY_ID and only extracts hex/rgb/hsl colors)
// and REBUILD the CSS from those validated parts. A tampered string either
// fails to parse (falls through to "not a pattern") or rebuilds into the exact
// same safe shape it started as — arbitrary bytes never reach the DOM.
export const resolvePatternWallpaperCss = (value) => {
  if (typeof value !== "string" || !isPatternCss(value)) return null;
  const parsed = parsePatternCss(value);
  if (!parsed || !isSafePatternColor(parsed.fg) || !isSafePatternColor(parsed.bg)) {
    return null;
  }
  const rebuilt = buildPatternCss(parsed);
  return rebuilt || null;
};

// The neutral gallery background == today's hard-coded surface. Keeping it here
// (not in the renderer) means "unset" and "explicitly neutral" resolve
// identically and the default can be swapped in one place later.
export const NEUTRAL_GALLERY_BACKGROUND = "#E8E8E8";

// Decision #gallery-card-white (WheelListing/styles.js / Profile/style.js's
// CardContainer): the profile card frame is pinned to pure white ALWAYS,
// regardless of the saved theme — only the wheels content panel takes the
// theme's Background colour. Any ink/contrast decision for content painted
// directly on the profile card (LinkVault border/text, the Follow "following"
// icon tint) must resolve against THIS, never the themed surface/wallpaper —
// shared by both pages/Gallery/index.jsx (the live page) and
// GalleryThemePreview.jsx (the Studio preview) so the two can never drift
// apart again (gordon-0913-vy-theme-preview-profile-card-parity).
export const GALLERY_CARD_BACKGROUND = "#FFFFFF";

// Neutral accent == the app PrimaryPurple. null => caller keeps its existing
// hard-coded value (a true passthrough).
export const NEUTRAL_GALLERY_ACCENT = null;
export const DEFAULT_ACCENT = "#9B5AF8";

// Advanced defaults.
export const DEFAULT_CARD_RADIUS = 12;
export const MIN_CARD_RADIUS = 0;
export const MAX_CARD_RADIUS = 32;
export const DEFAULT_WALLPAPER_OPACITY = 1;

// ── Curated presets (Preset looks: one tap, done) ───────────────────────────
// Concrete theme objects. A saved { preset:"iris" } "just works"; explicit
// free-form fields the user also set win over the preset (override seam below).
export const GALLERY_PRESETS = {
  iris: {
    label: "Iris",
    wallpaper: "linear-gradient(135deg, #EDE0FD 0%, #D4BBFF 100%)",
    accent: "#9B5AF8",
    bg_color: "#F6F3FF",
  },
  dusk: {
    label: "Dusk",
    wallpaper: "linear-gradient(135deg, #123051 0%, #29323C 100%)",
    accent: "#77CCCD",
    bg_color: "#1B2430",
  },
  bloom: {
    label: "Bloom",
    wallpaper: "linear-gradient(135deg, #F4C4F3 0%, #E9ADCA 100%)",
    accent: "#E25297",
    bg_color: "#FDF2F8",
  },
  // ISP-9999 (gordon-0913-gallery-theme-presets-looping-carousel): grow the
  // curated set from 3 to 12 so the new preset carousel has enough looks to
  // scroll through. Same schema as Iris/Dusk/Bloom above (untouched, byte-
  // identical); every wallpaper here is a two-stop gradient where both stops
  // share one luminance family (both light or both dark) so a single ink
  // choice (dark #123051 or white) stays AA-legible against either stop —
  // see galleryTheme.presets.contrast.test.js.
  slate: {
    label: "Slate",
    wallpaper: "linear-gradient(135deg, #E4E7EC 0%, #C7CDD6 100%)",
    accent: "#5B6B7C",
    bg_color: "#F4F5F7",
  },
  sunset: {
    label: "Sunset",
    wallpaper: "linear-gradient(135deg, #FFD9B3 0%, #FFAE7A 100%)",
    accent: "#FF7A45",
    bg_color: "#FFF3E9",
  },
  forest: {
    label: "Forest",
    wallpaper: "linear-gradient(135deg, #0F3D2E 0%, #1D5A43 100%)",
    accent: "#3FAE7B",
    bg_color: "#12261F",
  },
  ocean: {
    label: "Ocean",
    wallpaper: "linear-gradient(135deg, #0B3D5C 0%, #155E8A 100%)",
    accent: "#22A6B3",
    bg_color: "#0E2A3D",
  },
  mono: {
    label: "Mono",
    wallpaper: "linear-gradient(135deg, #E7E7E7 0%, #CFCFCF 100%)",
    accent: "#4A4A4A",
    bg_color: "#F2F2F2",
  },
  sand: {
    label: "Sand",
    wallpaper: "linear-gradient(135deg, #F2E4C8 0%, #E3CFA0 100%)",
    accent: "#C98A3E",
    bg_color: "#FBF3E3",
  },
  berry: {
    label: "Berry",
    wallpaper: "linear-gradient(135deg, #5A1442 0%, #7A1F5C 100%)",
    accent: "#E0559E",
    bg_color: "#2B0A20",
  },
  midnight: {
    label: "Midnight",
    wallpaper: "linear-gradient(135deg, #0B1024 0%, #161B3D 100%)",
    accent: "#5B6EE8",
    bg_color: "#0A0E1F",
  },
  mint: {
    label: "Mint",
    wallpaper: "linear-gradient(135deg, #D6F5E3 0%, #B4EBCB 100%)",
    accent: "#2FBF8F",
    bg_color: "#EAFBF2",
  },
};

// ── "Default" preset (Gordon law 2026-09-16: "the first Preset Theme is the
// iYuga default, labeled Default") ─────────────────────────────────────────
// Kept OUT of GALLERY_PRESETS on purpose: the curated presets are two-stop
// gradients with their own AA tests, and applying "Default" must NOT persist
// explicit colours (an explicit bg_color would paint the wheels panel, which
// the out-of-the-box Gallery never does). Its tile values are the exact
// fallbacks the live Gallery renders when no theme is applied:
//   wallpaper — NEUTRAL_GALLERY_BACKGROUND (above), the page fallback in
//               pages/Gallery/index.jsx (`themedGalleryBg || "#E8E8E8"`)
//   accent    — DEFAULT_ACCENT (above), the card-border fallback in
//               pages/Gallery/index.jsx (`resolveGalleryAccent(...) || DEFAULT_ACCENT`)
//   bg_color  — NEUTRAL_GALLERY_SURFACE, the wheels panel fallback in
//               WheelListing/FoldersV2/styles.js (`var(--gt-surface, colors.white)`)
export const DEFAULT_PRESET_ID = "default";
export const NEUTRAL_GALLERY_SURFACE = "#FFFFFF";
export const DEFAULT_GALLERY_PRESET = {
  label: "Default",
  wallpaper: NEUTRAL_GALLERY_BACKGROUND,
  accent: DEFAULT_ACCENT,
  bg_color: NEUTRAL_GALLERY_SURFACE,
};

// Apply a preset tile to a theme. "Default" clears the look back to the
// neutral passthrough (no stored wallpaper/accent/bg_color/preset), so the
// saved theme renders exactly like a profile that never picked a theme.
// Other fields (card_radius, match_link_vault) are kept, same as every preset.
export const applyPresetToTheme = (theme, presetId) => {
  const current = theme || {};
  if (presetId === DEFAULT_PRESET_ID) {
    return {
      ...current,
      preset: undefined,
      wallpaper: "",
      accent: undefined,
      bg_color: undefined,
    };
  }
  const p = GALLERY_PRESETS[presetId];
  if (!p) return current;
  return {
    ...current,
    preset: presetId,
    wallpaper: p.wallpaper,
    accent: p.accent,
    bg_color: p.bg_color,
  };
};

// Which preset tile shows as selected: the saved preset, else "Default" when
// no theme is applied at all.
export const resolveActivePresetId = (theme) => {
  const t = theme || {};
  if (t.preset && GALLERY_PRESETS[t.preset]) return t.preset;
  return isNeutralTheme(t) ? DEFAULT_PRESET_ID : undefined;
};

export const resolvePreset = (presetId) => {
  const p = presetId && GALLERY_PRESETS[presetId];
  if (!p) return null;
  const { label, ...theme } = p; // label is UI-only, never persisted/rendered
  return theme;
};

// ── CSS-injection guards ────────────────────────────────────────────────────
// Every theme value is USER-SUPPLIED and interpolated into inline CSS, so each
// must be validated before it reaches the DOM. These run only when the theming
// flag is ON (the renderer never calls them when OFF), so flag-OFF stays
// byte-identical.

// Strict color allowlist: hex (#RGB/#RRGGBB/#RRGGBBAA), rgb()/rgba(),
// hsl()/hsla(), oklch(). Fully anchored (^…$); inner classes permit only the
// chars legal inside each form — no ; { } < > quotes or url( can ride along.
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COLOR = /^rgba?\(\s*[0-9.,%\s/]+\)$/i;
const HSL_COLOR = /^hsla?\(\s*[0-9.,%\s/deg]+\)$/i;
const OKLCH_COLOR = /^oklch\(\s*[0-9.,%\s/]+\)$/i;

// True when `v` is a single, safe CSS color literal we will inline.
export const isSafeCssColor = (v) => {
  if (typeof v !== "string") return false;
  const s = v.trim();
  if (!s || s.length > 50) return false;
  return (
    HEX_COLOR.test(s) ||
    RGB_COLOR.test(s) ||
    HSL_COLOR.test(s) ||
    OKLCH_COLOR.test(s)
  );
};

// A background image value (gradient or css-pattern). It legitimately contains
// parens/commas/percentages so it can't pass isSafeCssColor; allow the gradient
// functions but reject anything that could break out of the declaration
// (; { } < > quotes control-chars url().
// eslint-disable-next-line no-control-regex
const BG_BREAKOUT = /[;{}<>"']|url\s*\(|[\x00-\x1f]/i;
export const isSafeGradient = (v) =>
  typeof v === "string" &&
  v.length <= 2000 &&
  /(linear|radial|conic|repeating-linear|repeating-radial)-gradient\s*\(/i.test(
    v
  ) &&
  !BG_BREAKOUT.test(v);

// Any background value we'll inline: a safe solid color OR a safe gradient.
export const isSafeCssBackground = (v) =>
  isSafeCssColor(v) || isSafeGradient(v);

// Validate a user-supplied http(s) wallpaper URL. Returns the href when it is a
// well-formed http(s) URL free of CSS-breakout characters, else null.
export const sanitizeHttpWallpaper = (raw) => {
  if (typeof raw !== "string" || raw.length > 2000) return null;
  // eslint-disable-next-line no-control-regex
  if (/["'()<>;]|[\x00-\x1f]/.test(raw)) return null;
  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  return url.href;
};

// ── Image wallpaper media reference ─────────────────────────────────────────
// The Image tab's Upload / Media Library / Recent / Embed URL sources all
// resolve to a real media reference (mirroring the builder's upload flow —
// uploadFile()'s return value), not a bare pasted string. `source` is
// informational only (never trusted for rendering).
export const isImageWallpaperRef = (w) =>
  !!w && typeof w === "object" && typeof w.url === "string";

// Validate + strip an image wallpaper ref down to the safe
// {url, source?, display_url?} shape, or null when the url isn't a safe
// http(s) href.
//
// T8.10 ("Wallpaper arrives ~1.2 s after the cards, gray first"): `url` is the
// creator's raw upload -- measured live on web-stg, a 4032x3024 / 6.26 MB
// camera JPEG the Gallery painted 1:1, so the cards sat on the gray page for
// ~1.5 s at 40 Mbps before the photo landed. The backend now records a
// <=1920 px copy as `display_url` (Backend home/gallery_wallpaper_display.py);
// it is kept here (same http(s) guard) so the page can paint it, while `url`
// stays the real asset the studio/media library work with.
export const sanitizeImageWallpaperRef = (w) => {
  if (!isImageWallpaperRef(w)) return null;
  const safeUrl = sanitizeHttpWallpaper(w.url);
  if (!safeUrl) return null;
  const source =
    typeof w.source === "string" && w.source.length <= 30 ? w.source : undefined;
  const displayUrl =
    w.display_url != null ? sanitizeHttpWallpaper(w.display_url) : null;
  return {
    url: safeUrl,
    ...(source ? { source } : {}),
    ...(displayUrl ? { display_url: displayUrl } : {}),
  };
};

// ISP-9999 (gordon-0923-P2-gallery-theme-reopen-on-saved-wallpaper-type-tab):
// classify a saved wallpaper value into the WALLPAPER_TABS id (constants.js:
// color/gradient/pattern/image) that actually produced it, so the popup's
// Wallpaper tab strip can reopen on the matching tab instead of a hardcoded
// default. Order matters: an image ref is an object (never a gradient/pattern
// string), a pattern CSS string fails isSafeGradient's gradient-function
// test, so checking image -> pattern -> gradient -> color (fallback) never
// double-matches.
export const resolveWallpaperTab = (wallpaper) => {
  if (isImageWallpaperRef(wallpaper)) return "image";
  if (isPatternCss(wallpaper)) return "pattern";
  if (isSafeGradient(wallpaper)) return "gradient";
  return "color";
};

// Clamp a numeric advanced value into [min,max]; fall back when not finite.
export const clampNumber = (v, min, max, fallback) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

// ── Normalisation ───────────────────────────────────────────────────────────
// Normalise an arbitrary persisted value into a safe theme object. Preset values
// are the base; explicit free-form fields the user also set win (override seam).
export const normalizeGalleryTheme = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  const base = resolvePreset(raw.preset) || {};
  // wallpaper is either a string (color/gradient/pattern-encoded/legacy URL) or
  // a media-ref object {url, source?} (Image tab, real upload/library/recent/
  // embed selection). An object that fails sanitation is dropped, not carried
  // through as garbage — legacy plain strings pass through untouched here and
  // are validated later at resolve time (same convention as before this unit).
  const normalizedWallpaper =
    raw.wallpaper != null
      ? typeof raw.wallpaper === "object"
        ? sanitizeImageWallpaperRef(raw.wallpaper) || undefined
        : raw.wallpaper
      : undefined;
  const out = {
    ...base,
    ...(raw.preset ? { preset: raw.preset } : {}),
    ...(normalizedWallpaper != null ? { wallpaper: normalizedWallpaper } : {}),
    ...(raw.accent ? { accent: raw.accent } : {}),
    ...(raw.bg_color ? { bg_color: raw.bg_color } : {}),
  };
  if (raw.card_radius != null) {
    out.card_radius = clampNumber(
      raw.card_radius,
      MIN_CARD_RADIUS,
      MAX_CARD_RADIUS,
      DEFAULT_CARD_RADIUS
    );
  }
  // Opacity control removed (ISP-9999, Gordon #opacity-sliders 2026-08-31): a
  // legacy raw.wallpaper_opacity is deliberately NOT carried through here, so
  // any previously-saved value is harmless -- every downstream resolver below
  // falls back to its "unset" branch and renders as if opacity were 100%.
  if (raw.match_link_vault) out.match_link_vault = true;
  return out;
};

// True when a theme has no effective styling (renders today's exact Gallery).
export const isNeutralTheme = (theme) => {
  const t = normalizeGalleryTheme(theme);
  return (
    !t.preset &&
    !t.wallpaper &&
    !t.accent &&
    !t.bg_color &&
    !t.match_link_vault
  );
};

// ── Resolvers (what the renderer consumes) ──────────────────────────────────
// Resolve the CSS `background` value for the Gallery surface.
// Priority: explicit wallpaper (gradient/pattern/image url) > solid bg_color.
// Returns NULL when nothing is set (neutral) so the caller keeps today's EXACT
// look — a true passthrough, not a re-painted "neutral" surface.
export const resolveGalleryBackground = (theme) => {
  const t = normalizeGalleryTheme(theme);
  if (t.wallpaper) {
    if (isImageWallpaperRef(t.wallpaper)) {
      const safeRef = sanitizeImageWallpaperRef(t.wallpaper);
      // T8.10: paint the web-sized display copy when the backend made one.
      if (safeRef)
        return `url("${safeRef.display_url || safeRef.url}") center / cover no-repeat`;
    } else if (typeof t.wallpaper === "string") {
      const patternCss = resolvePatternWallpaperCss(t.wallpaper);
      if (patternCss) {
        return patternCss;
      } else if (/^https?:\/\//i.test(t.wallpaper)) {
        const safeUrl = sanitizeHttpWallpaper(t.wallpaper);
        if (safeUrl) return `url("${safeUrl}") center / cover no-repeat`;
      } else if (isSafeCssBackground(t.wallpaper)) {
        return t.wallpaper;
      }
    }
    // Unsafe/unset wallpaper: fall through to bg_color / neutral passthrough.
  }
  if (t.bg_color && isSafeCssBackground(t.bg_color)) return t.bg_color;
  return null;
};

// Compose the FINAL CSS `background` for the public Gallery surface, folding the
// saved wallpaper opacity into a white veil layer ON TOP of the wallpaper.
//
// QA-112: the live gallery previously painted only `resolveGalleryBackground`,
// so a saved `wallpaper_opacity` never rendered — "it only changes the gallery
// background but doesn't change opacity" (Kelly). We can't set `opacity` on the
// gallery element itself (that would fade its content too), so we composite: a
// semi-transparent white top layer over the OPAQUE wallpaper bottom layer. The
// result is itself an opaque paint, so nesting two themed elements never
// compounds the veil (the child fully occludes the parent where they overlap).
//
// Returns null for neutral passthrough (caller keeps today's EXACT surface).
export const resolveGalleryBackgroundLayered = (theme) => {
  const bg = resolveGalleryBackground(theme);
  if (!bg) return null;
  const opacity = resolveWallpaperOpacity(theme);
  if (!(opacity < 1)) return bg; // opaque (or unset default) -> raw wallpaper
  const veil = clampNumber(1 - opacity, 0, 1, 0);
  return `linear-gradient(rgba(255,255,255,${veil}), rgba(255,255,255,${veil})), ${bg}`;
};

// Resolve the accent color. Null => caller keeps its existing default, so unset
// is a true passthrough. accent is inlined into borders/emphasis, so only a
// strict single-color literal is allowed.
export const resolveGalleryAccent = (theme) => {
  const t = normalizeGalleryTheme(theme);
  return isSafeCssColor(t.accent) ? t.accent : NEUTRAL_GALLERY_ACCENT;
};

// Resolve the solid surface color (used behind cards / for contrast decisions).
export const resolveGallerySurface = (theme) => {
  const t = normalizeGalleryTheme(theme);
  if (t.bg_color && isSafeCssColor(t.bg_color)) return t.bg_color;
  return null;
};

export const resolveCardRadius = (theme) => {
  const t = normalizeGalleryTheme(theme);
  return t.card_radius != null ? t.card_radius : DEFAULT_CARD_RADIUS;
};

// gordon-0917-themes-popup-add-business-box-and-linkvault follow-up
// (batch-proof-fe-3d5e0fc5, Gordon 2026-09-17): Theming/themeEngine.js's
// resolveMatchGallery expects the NEWER generalized token theme shape
// ({background:{type,value}, button:{fill,border}, surface, card,
// corner_rounding} -- see that file's own header comment). UserProfile
// .gallery_theme has only ever been persisted in THIS file's older, unrelated
// shape (bg_color/wallpaper/accent/preset/card_radius -- see the header at
// the top of this file) -- these are two independently-evolved theme
// engines that were never bridged. Business Box's "Match the Gallery"
// (DataBox.jsx, BusinessBoxThemePreview.jsx) called resolveMatchGallery with
// the RAW legacy-shaped gallery_theme, so normalizeTheme found no
// `.background`/`.button`/`.surface` keys on it and resolved every matched
// token to null for every real account -- the toggle silently did nothing
// (or, for a token whose match flag was on, replaced the surface's own
// value with null instead of the gallery's). Bridge into the shape
// resolveMatchGallery actually expects, using the SAME resolvers the real
// Gallery page (src/pages/Gallery/index.jsx) already renders from.
export const galleryThemeAsMatchGallerySource = (theme) => {
  const background = resolveGalleryBackground(theme);
  const accent = resolveGalleryAccent(theme);
  const surface = resolveGallerySurface(theme);
  // `accent` doubles as both the CTA/button colour AND the card border --
  // per this file's own resolveGalleryAccent doc comment ("CTA card border /
  // emphasis"), the real Gallery page has only ONE accent concept for both
  // (see --gt-card-border in src/pages/Gallery/index.jsx, also
  // resolveGalleryAccent).
  return {
    background: background ? { type: "color", value: background } : null,
    button: accent ? { fill: accent, border: null } : null,
    surface: surface || accent ? { fill: surface || null, border: accent || null } : null,
    card: { fill: GALLERY_CARD_BACKGROUND, border: null },
    corner_rounding: resolveCardRadius(theme),
  };
};

// Opacity control removed (ISP-9999): normalizeGalleryTheme never populates
// t.wallpaper_opacity any more, so this always falls through to 1 (100%) --
// including for profiles with a pre-existing saved value.
export const resolveWallpaperOpacity = (theme) => {
  const t = normalizeGalleryTheme(theme);
  return t.wallpaper_opacity != null ? t.wallpaper_opacity : 1;
};

// Compose the ACTUAL Gallery-page background string, honouring wallpaper_opacity.
// The studio's live preview fades the wallpaper with a white veil at
// (1 - wallpaper_opacity); the real Gallery previously applied only the raw
// background (resolveGalleryBackground) and ignored opacity entirely, so a saved
// opacity change never showed on the live page — the QA-112 "opacity is dropped"
// report. This composites the SAME veil as a single background value (works for a
// solid colour, a gradient, or an image wallpaper — the veil is the top layer,
// the wallpaper the bottom), so preview and live match.
// Returns null when neutral (caller keeps today's #E8E8E8 passthrough → unchanged
// when the theming flag is OFF or no wallpaper is set).
export const composeGalleryPageBackground = (theme) => {
  const bg = resolveGalleryBackground(theme);
  if (!bg) return null;
  const opacity = resolveWallpaperOpacity(theme);
  if (!(opacity < 1)) return bg; // fully opaque (or unset) → raw wallpaper, byte-identical
  const alpha = Math.max(0, Math.min(1, 1 - opacity)).toFixed(3);
  const veil = `rgba(255,255,255,${alpha})`;
  return `linear-gradient(${veil}, ${veil}), ${bg}`;
};

// Resolve the theme actually rendered once "Match Link Vault" is folded in.
// Extracted so every consumer that needs pattern/background/accent to agree on
// the SAME effective theme (studio preview, public Gallery, css vars below)
// shares one merge instead of three copies of the same fallback logic.
export const resolveEffectiveGalleryTheme = (theme, vaultTheme) => {
  const effective = normalizeGalleryTheme(theme);
  // Missing-aspect re-integration: "Match Link Vault" inherits the already-live
  // vault_theme so the two surfaces stay in sync when the creator opts in.
  if (effective.match_link_vault && vaultTheme && typeof vaultTheme === "object") {
    return {
      ...effective,
      wallpaper: effective.wallpaper || vaultTheme.wallpaper,
      accent: effective.accent || vaultTheme.accent,
      bg_color: effective.bg_color || vaultTheme.bg_color,
    };
  }
  return effective;
};

// Build the flat set of CSS custom properties the renderer/preview applies.
// Only ever emits values that passed the guards. Empty object === passthrough.
export const resolveGalleryCssVars = (theme, vaultTheme) => {
  const effective = resolveEffectiveGalleryTheme(theme, vaultTheme);
  const vars = {};
  const bg = resolveGalleryBackground(effective);
  if (bg) vars["--gt-bg"] = bg;
  const surface = resolveGallerySurface(effective);
  if (surface) vars["--gt-surface"] = surface;
  const accent = resolveGalleryAccent(effective);
  if (accent) vars["--gt-accent"] = accent;
  vars["--gt-card-radius"] = `${resolveCardRadius(effective)}px`;
  vars["--gt-wallpaper-opacity"] = String(resolveWallpaperOpacity(effective));
  if (accent) {
    vars["--gt-accent-ink"] = resolveGalleryCardAccentInk(effective, {
      largeText: true,
      minVisible: true,
    });
    vars["--gt-accent-ink-text"] = resolveGalleryCardAccentInk(effective, { largeText: false });
    // gordon-0918c-accent-on-small-surfaces-legible-family-color-toast: the
    // Studio preview must read the SAME six small-surface accents the live
    // Gallery page emits (pages/Gallery/index.jsx), or the preview and the
    // real page can silently drift onto different answers again.
    const small = resolveSmallSurfaceAccents(effective);
    vars["--gt-lv-chip-border"] = small.linkVaultChipBorder;
    vars["--gt-desc-scrollbar-thumb"] = small.descScrollbarThumb;
    vars["--gt-search-border"] = small.searchFieldBorder;
    vars["--gt-collections-scrollbar-thumb"] = small.collectionsScrollbarThumb;
    vars["--gt-tab-accent"] = small.tabAccent;
    vars["--gt-tab-hover-fill"] = small.tabHoverFill;
    vars["--gt-create-wheel-border"] = small.createWheelBorder;
  }
  if (bg || surface) {
    // gordon-0917-gallery-theme-auto-contrast-text-and-icons: same resolved
    // value as --gt-ink — kept as a distinct var name so icon call sites
    // (Profile pencil/kebab, Collections count badge) self-document why
    // they're reading it, not because the computation differs.
    vars["--gt-ink"] = resolveGalleryTextInk(effective);
    vars["--gt-icon"] = vars["--gt-ink"];
  }
  return vars;
};

// Choose readable ink for text sitting on a resolved surface (WCAG-ish).
// Returns a dark or light ink hex; used so preset "Dusk" (dark) keeps legible
// text without the user hand-tuning it.
//
// gordon-0913-gallery-theme-auto-contrast-and-follow-icons: now a thin wrapper
// over the shared WCAG contrast-ratio helper (utils/contrast.js's
// autoTextColor — the same one the "blocks default to segment color" auto-
// contrast law already uses for Buttons/Form blocks and the Discover favorite
// heart) instead of its own approximate luminance formula, so every themed
// surface in the app picks legible ink the same way. Same public contract
// (dark "#123051" / light "#FFFFFF" / null when unresolvable).
export const readableInkFor = (bgColor) => {
  if (!isSafeCssColor(bgColor) || bgColor[0] !== "#") return null;
  return autoTextColor(bgColor, { fallback: null });
};

// The single background gallery-theme contrast decisions are made against:
// the resolved solid surface color when the creator set one, else the
// resolved wallpaper's effective color, else the neutral gallery background.
// Always returns a real hex (never null) so callers never have to special-
// case an unresolvable background.
export const resolveGalleryEffectiveBackground = (theme) => {
  const t = normalizeGalleryTheme(theme);
  return (
    effectiveBgHex(resolveGallerySurface(t)) ||
    effectiveBgHex(resolveGalleryBackground(t)) ||
    NEUTRAL_GALLERY_BACKGROUND
  );
};

// Auto-contrasted ink for TEXT that isn't accent-branded (stats/bio/name) —
// always picks whichever of dark/light ink reads best against the resolved
// background (D-11's autoTextColor, same helper as readableInkFor above).
export const resolveGalleryTextInk = (theme) =>
  autoTextColor(resolveGalleryEffectiveBackground(theme), {
    dark: INK_TEXT,
    light: WHITE_TEXT,
    fallback: INK_TEXT,
  });

// Auto-contrasted ink for ACCENT-branded elements (LinkVault border/text, the
// Follow icon's "following" tint): paints as the real accent whenever it
// already clears AA against the resolved background, and only swaps to an
// auto dark/light ink when the creator's accent/background choice would
// otherwise go invisible (Gordon 2026-09-13: "when Accent colour == Background
// colour ... auto-switch to a light or dark ink"). opts.largeText picks the
// 3:1 (icon/border) vs 4.5:1 (text) threshold.
export const resolveGalleryAccentInk = (theme, opts = {}) => {
  const accent = resolveGalleryAccent(theme) || DEFAULT_ACCENT;
  const bg = resolveGalleryEffectiveBackground(theme);
  return autoAccentInk(accent, bg, opts);
};

// Auto-contrasted ink for accent-branded elements that render INSIDE the
// profile card specifically (LinkVault border/text, the Follow "following"
// icon tint) — unlike resolveGalleryAccentInk above (which contrasts against
// the themed page/panel surface), these always sit on GALLERY_CARD_BACKGROUND
// (decision #gallery-card-white: the profile card never takes the theme's
// Background colour, see the constant's doc comment). The single shared
// resolver both pages/Gallery/index.jsx (live) and resolveGalleryCssVars below
// (Studio preview) call, so the two can't independently drift onto two
// different contrast baselines again (gordon-0913-vy-theme-preview-profile-
// card-parity).
export const resolveGalleryCardAccentInk = (theme, opts = {}) => {
  const accent = resolveGalleryAccent(theme) || DEFAULT_ACCENT;
  // Gordon 2026-09-16 (Themes item 1): the profile card now paints the theme
  // Background, so accent ink is checked against that surface (white if unset).
  return autoAccentInk(accent, resolveGallerySurface(theme) || GALLERY_CARD_BACKGROUND, opts);
};

// gordon-0918c-accent-on-small-surfaces-legible-family-color-toast: the
// shared GalleryHeader (Header/styles.js) paints its OWN fixed background —
// it never takes the Gallery theme's surface colour — so the Create Wheel
// button border's legibility check must be against THIS, not --gt-surface.
export const HEADER_SURFACE_BACKGROUND = colors.background; // #F6F7F9

// gordon-0918c: exact toast copy, shared so the trigger site and its tests
// can't drift onto two different strings.
export const ACCENT_LEGIBILITY_TOAST_COPY =
  "Accent color is being adjusted on some surfaces for legibility";

// gordon-0918c-accent-on-small-surfaces-legible-family-color-toast: "would be
// cool if these also took on the Accent color... if someone selects White
// for the Accent, then the scroll bar for the Profile Description or the
// Collections Slider would be unreadable... find the CLOSEST legible color in
// the accent family". Six MORE (thinner/smaller) surfaces beyond the four big
// decorative card/space borders (gordon-0918b, WCAG_MIN_VISIBLE 1.5:1) now
// take the accent too, at the stricter WCAG non-text 3:1 floor
// (WCAG_AA_LARGE) — a 1-2px hairline border or a thin scrollbar thumb
// genuinely disappears at the looser 1.5:1 floor the big borders use. Each
// surface resolves legibleAccent (same-hue family, never a color-family jump)
// against ITS OWN background:
//   - the profile card is always pinned white regardless of theme (decision
//     #gallery-card-white) -> LinkVault chip border + Description scrollbar
//   - the wheels/collections/search panel takes the themed surface
//     (--gt-surface) -> search border, Collections scrollbar, tab
//     underline/count chip
//   - the header never themes at all -> Create Wheel button border
// One resolver so the live page (pages/Gallery/index.jsx), the Studio preview
// (resolveGalleryCssVars below) and the toast trigger (GalleryThemeStudio)
// can never independently drift onto three different answers.
//
// gordon-0921 (owner design rule, verbatim: "on-hover highlight effects
// should match the Accent Color ... a pale red instead of the purple"):
// `tabHoverFill` extends this SAME resolver with the tab strip's pale
// hover/selected fill (CollectionTab's ::before pill) — a ~12% alpha tint of
// the RAW accent (not the contrast-walked `tabAccent`, since a translucent
// fill under body text doesn't carry its own AA obligation the way the
// underline/label text does) over the panel surface. Kept in the same
// function as tabAccent so a later generalization sweep
// (gordon-0921-WEB-gallery-theme-hover-and-selected-highlights-...) has ONE
// place to adopt, not two independently-drifting color decisions.
const GALLERY_TAB_HOVER_FILL_ALPHA = 0.12;

// gordon-0921-WEB-gallery-theme-hover-and-selected-highlights-must-use-the-
// theme-accent-colour-sweep: this IS that later generalization. Every OTHER
// interactive element in the gallery (collection tiles, filter chips, the
// view toggle, sort/filter dropdowns, profile card buttons, the search
// focus ring, wheel-card actions, etc.) needs the exact same "pale accent
// tint for a filled hover/selected background" `tabHoverFill` already
// resolves for the tab strip — so `hoverFill` below is that SAME
// hexToRgba(accent, GALLERY_TAB_HOVER_FILL_ALPHA) value, generalized under a
// non-tab-specific name and emitted as its own CSS var (--gt-hover-fill) so
// every consumer in this sweep reads ONE shared value, not a second
// independently-drifting pale-tint formula.
//
// `accentOnFillText` covers the OTHER half of the owner's rule ("Text on any
// highlight keeps WCAG AA 4.5:1 through the shared auto-contrast helper —
// never a hand-picked hex"): for a SOLID accent fill (e.g. a selected filter
// chip painting `--gt-accent` as its own background, not the pale tint), the
// label text must contrast against the ACCENT ITSELF, not against the white
// card surface --gt-accent-ink-text already checks. Reuses the exact same
// D-11 helper (utils/contrast.js autoTextColor) CollectionTabCount's
// collectionTabCountTextColor already uses for the analogous "text on my own
// opaque fill" problem, just with the accent (not a fixed pill hex) as that
// fill -- but with BLACK_TEXT (not the branded INK_TEXT) as the "dark"
// candidate: PROVE-CAN-FAIL measured that INK_TEXT/WHITE_TEXT both fall
// below 4.5:1 against a midtone accent (the brand purple DEFAULT_ACCENT
// itself: ~4.03:1; a red theme accent: ~4.23:1) -- BLACK_TEXT paired with
// WHITE_TEXT is mathematically guaranteed >= ~4.58:1 against ANY background
// (see BLACK_TEXT's doc comment in utils/contrast.js), so this is the one
// case that must NOT reuse the app's branded ink.
export const resolveGalleryAccentPale = (
  theme,
  alphaValue = GALLERY_TAB_HOVER_FILL_ALPHA
) => {
  const accent = resolveGalleryAccent(theme) || DEFAULT_ACCENT;
  return (
    hexToRgba(accent, alphaValue) || hexToRgba(DEFAULT_ACCENT, alphaValue)
  );
};

export const resolveGalleryAccentOnFillText = (theme) => {
  const accent = resolveGalleryAccent(theme) || DEFAULT_ACCENT;
  return autoTextColor(accent, {
    dark: BLACK_TEXT,
    light: WHITE_TEXT,
    fallback: WHITE_TEXT,
  });
};

export const resolveSmallSurfaceAccents = (theme) => {
  const accent = resolveGalleryAccent(theme) || DEFAULT_ACCENT;
  const cardBg = GALLERY_CARD_BACKGROUND;
  const panelBg = resolveGallerySurface(theme) || GALLERY_CARD_BACKGROUND;
  const headerBg = HEADER_SURFACE_BACKGROUND;
  const result = {
    linkVaultChipBorder: legibleAccent(accent, cardBg, WCAG_AA_LARGE),
    descScrollbarThumb: legibleAccent(accent, cardBg, WCAG_AA_LARGE),
    searchFieldBorder: legibleAccent(accent, panelBg, WCAG_AA_LARGE),
    collectionsScrollbarThumb: legibleAccent(accent, panelBg, WCAG_AA_LARGE),
    tabAccent: legibleAccent(accent, panelBg, WCAG_AA_LARGE),
    tabHoverFill: hexToRgba(accent, GALLERY_TAB_HOVER_FILL_ALPHA),
    createWheelBorder: legibleAccent(accent, headerBg, WCAG_AA_LARGE),
    // gordon-0921 generalization sweep — see doc comment above.
    hoverFill: hexToRgba(accent, GALLERY_TAB_HOVER_FILL_ALPHA),
    accentOnFillText: resolveGalleryAccentOnFillText(theme),
  };
  // tabHoverFill/hoverFill are deliberately excluded: they're always an
  // rgba() tint (never a passthrough of the raw accent hex), so comparing
  // them against `accent` would flag anyAdjusted=true unconditionally and
  // defeat the flag's whole purpose (surfacing when a creator's own accent
  // had to be adjusted). accentOnFillText is excluded for the same reason —
  // it's an ink color picked to sit ON the accent, never the accent itself.
  const PASSTHROUGH_EXEMPT = new Set(["tabHoverFill", "hoverFill", "accentOnFillText"]);
  result.anyAdjusted = Object.entries(result).some(
    ([key, resolved]) =>
      !PASSTHROUGH_EXEMPT.has(key) && resolved.toLowerCase() !== accent.toLowerCase()
  );
  return result;
};
